import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Search, Truck, ChevronRight, Printer, Ban, Pencil, Package } from "lucide-react";
import { toast } from "sonner";

import { formatDate, formatDateTime } from "@/lib/datetime";
const STATUS_TONE = {
  draft: "bg-gray-100 text-gray-700 border-gray-200",
  approved: "bg-blue-50 text-blue-800 border-blue-200",
  received: "bg-emerald-50 text-emerald-800 border-emerald-200",
  cancelled: "bg-red-50 text-red-800 border-red-200",
};

export default function StockTransfers() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    try {
      const params = { page: 1, page_size: 100 };
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/sales/stock-transfers", { params });
      let list = r.data.items || [];
      if (search) {
        const s = search.toLowerCase();
        list = list.filter((x) =>
          (x.transfer_no || "").toLowerCase().includes(s) ||
          (x.source?.name || "").toLowerCase().includes(s) ||
          (x.dest?.name || "").toLowerCase().includes(s)
        );
      }
      setItems(list);
      setTotal(r.data.total || 0);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [statusFilter, search]);
  useEffect(() => { load(); }, [load]);

  const cancel = async (tr) => {
    if (!window.confirm(`Cancel transfer ${tr.transfer_no || "(draft)"}? Any stock movements will be reversed.`)) return;
    try { await api.post(`/sales/stock-transfers/${tr.id}/cancel`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cancel failed"); }
  };
  const receive = async (tr) => {
    if (!window.confirm(`Mark ${tr.transfer_no} as received? Destination stock will be added.`)) return;
    try { await api.post(`/sales/stock-transfers/${tr.id}/receive`); toast.success("Received — stock updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Receive failed"); }
  };

  return (
    <div className="space-y-4">
      <PageSection
        title="Stock Transfers"
        description="Move stock between Warehouses and Branches / Franchises (same GSTIN only). Draft → Approve (delivery challan issued, source stock deducted) → Received (destination stock added)."
        action={<Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={() => nav("/sales/stock-transfers/new")} data-testid="tr-new-btn"><Plus className="h-4 w-4" /> New Transfer</Button>}
      >
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 max-w-xs min-w-[220px]">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Transfer # / source / dest…" className="pl-8" data-testid="tr-search" />
            </div>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-md border border-gray-200 text-sm px-2" data-testid="tr-status-filter">
              <option value="">All status</option>
              <option value="draft">Draft</option>
              <option value="approved">Approved</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <span className="ml-auto text-xs text-gray-500">{total.toLocaleString()} transfer(s)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Transfer #</th>
                <th className="px-3 py-2">Date</th>
                <th className="px-3 py-2">Source → Dest</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Qty</th>
                <th className="px-3 py-2">Transporter</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-10 text-center text-gray-400"><Truck className="h-8 w-8 mx-auto mb-2 opacity-30" /> No transfers.</td></tr>
              )}
              {items.map((tr) => (
                <tr key={tr.id} className={`hover:bg-gray-50 ${tr.status === "cancelled" ? "opacity-70" : ""}`} data-testid={`tr-row-${tr.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{tr.transfer_no || <span className="text-gray-400">(draft)</span>}</td>
                  <td className="px-3 py-2 text-xs">{formatDate(tr.transfer_date)}</td>
                  <td className="px-3 py-2 text-xs">
                    <div className="flex items-center gap-1">
                      <span className="text-gray-700">{tr.source?.name}</span>
                      <ChevronRight className="h-3 w-3 text-gray-400" />
                      <span className="font-medium">{tr.dest?.name}</span>
                    </div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{tr.source?.type} → {tr.dest?.type}</div>
                  </td>
                  <td className="px-3 py-2"><span className={`text-[10px] uppercase tracking-widest font-semibold border rounded px-2 py-0.5 ${STATUS_TONE[tr.status] || ""}`}>{tr.status}</span></td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold">{tr.total_qty}</td>
                  <td className="px-3 py-2 text-xs text-gray-700">{tr.transporter || "—"}{tr.vehicle_no ? ` · ${tr.vehicle_no}` : ""}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {tr.status === "draft" && (
                      <button className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center" onClick={() => nav(`/sales/stock-transfers/${tr.id}/edit`)} title="Edit" data-testid={`tr-edit-${tr.id}`}>
                        <Pencil className="h-3.5 w-3.5 text-gray-500" />
                      </button>
                    )}
                    {["approved", "received", "cancelled"].includes(tr.status) && tr.transfer_no && (
                      <a className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center" href={`/sales/stock-transfers/${tr.id}/challan`} target="_blank" rel="noreferrer" title="Delivery Challan" data-testid={`tr-print-${tr.id}`}>
                        <Printer className="h-3.5 w-3.5 text-gray-500" />
                      </a>
                    )}
                    {tr.status === "approved" && (
                      <button className="h-7 w-7 rounded hover:bg-emerald-50 inline-flex items-center justify-center ml-1" onClick={() => receive(tr)} title="Mark Received" data-testid={`tr-receive-${tr.id}`}>
                        <Package className="h-3.5 w-3.5 text-emerald-700" />
                      </button>
                    )}
                    {tr.status !== "cancelled" && (
                      <button className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center ml-1" onClick={() => cancel(tr)} title="Cancel" data-testid={`tr-cancel-${tr.id}`}>
                        <Ban className="h-3.5 w-3.5 text-red-500" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      </PageSection>
    </div>
  );
}
